import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";

function getTrialEndDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function SignupConfirmation() {
  const location = useLocation();
  const email = location.state?.email || "[your email]";
  const trialEndDate = location.state?.trialEndDate || getTrialEndDate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="confirmation-page">
      {/* Minimal top bar */}
      <div className="border-b border-gray-100 bg-white">
        <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading text-xl font-bold text-[#20611B] tracking-tight"
            data-testid="confirmation-logo"
          >
            {BRAND.name}
          </Link>
          <Link
            to="/"
            className="text-sm text-[#6B7280] hover:text-[#1A1A1A] transition-colors"
            data-testid="confirmation-back"
          >
            Back to website
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-12 md:py-16">
        {/* Success icon */}
        <div className="flex justify-center mb-6">
          <CheckCircle
            className="h-16 w-16 text-[#20611B]"
            data-testid="success-icon"
          />
        </div>

        <h1
          className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] text-center tracking-tight mb-3"
          data-testid="confirmation-heading"
        >
          You are all set.
        </h1>

        <p className="text-[#6B7280] text-base text-center mb-10 max-w-md mx-auto">
          Your 7-day free trial of Epsillon Leads is now active. Here is what
          happens next.
        </p>

        {/* Steps */}
        <div className="space-y-8 mb-10">
          {/* Step 1 */}
          <div
            className="flex gap-4"
            data-testid="confirmation-step-1"
          >
            <div className="w-8 h-8 rounded-full bg-[#D8FFB8] flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-sm font-bold text-[#20611B]">1</span>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-[#1A1A1A] mb-1">
                Check your inbox
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">
                We have sent a welcome email to{" "}
                <span className="font-medium text-[#1A1A1A]">{email}</span>. It
                includes your login details and a quick-start guide.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div
            className="flex gap-4"
            data-testid="confirmation-step-2"
          >
            <div className="w-8 h-8 rounded-full bg-[#D8FFB8] flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-sm font-bold text-[#20611B]">2</span>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-[#1A1A1A] mb-1">
                Download the mobile app
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed mb-3">
                Get the Epsillon Leads app on your phone so you never miss a
                lead.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Replace with actual App Store URL */}
                <a
                  href="#"
                  data-testid="app-store-badge"
                  className="inline-flex items-center justify-center h-10 px-5 bg-[#1A1A1A] text-white text-xs font-medium rounded-lg hover:bg-[#333] transition-colors"
                >
                  Download on the App Store
                </a>
                {/* Replace with actual Play Store URL */}
                <a
                  href="#"
                  data-testid="play-store-badge"
                  className="inline-flex items-center justify-center h-10 px-5 bg-[#1A1A1A] text-white text-xs font-medium rounded-lg hover:bg-[#333] transition-colors"
                >
                  Get it on Google Play
                </a>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div
            className="flex gap-4"
            data-testid="confirmation-step-3"
          >
            <div className="w-8 h-8 rounded-full bg-[#D8FFB8] flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-sm font-bold text-[#20611B]">3</span>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-[#1A1A1A] mb-1">
                Book your onboarding call
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed mb-3">
                Want a walkthrough of the platform? Book a free 15-minute
                onboarding call with our team.
              </p>
              {/* Replace with Calendly or booking URL */}
              <a href="#" data-testid="onboarding-cta">
                <Button className="bg-[#20611B] hover:bg-[#174d14] text-white font-bold h-10 px-6 rounded-md text-sm tracking-wide transition-all">
                  Book Onboarding Call
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8" />

        {/* Support info */}
        <p className="text-sm text-[#6B7280] text-center mb-3">
          Need help? Email us at{" "}
          <a
            href="mailto:hello@epsillonmedia.com.au"
            className="text-[#20611B] underline"
          >
            hello@epsillonmedia.com.au
          </a>{" "}
          or call [phone placeholder].
        </p>

        <p className="text-xs text-[#6B7280] text-center">
          Your first payment of $299 AUD will be charged on{" "}
          <span className="font-medium">{trialEndDate}</span>. You will receive
          a reminder email before you are charged.
        </p>
      </div>
    </div>
  );
}
